/**
 * callbacks API client — Story 7.13b → 9.7 refactor.
 *
 * Typed wrappers around `apiClient.{get,post}` for the `/admin/callbacks*`
 * endpoints. Types vêm do shared package `@sales-agent/admin-schemas/callbacks`
 * (Story 9.7) — single source of truth com backend; replicação manual
 * removida.
 *
 * RBAC reminder (enforced backend-side):
 *   - GET list / GET detail   → viewer | operator | admin
 *   - POST :id/cancel         → operator | admin
 *   - POST :id/retry          → admin
 *
 * Phone is always returned masked from the backend (`maskedPhone`); for admin
 * role the detail endpoint returns full digits in the same field.
 */
import type {
  CallbackExecutionStatus,
  CallbackListItemT,
  CallbackListResponseT,
  CallbackDetailT,
  CallbackMessagePreviewT,
} from '@sales-agent/admin-schemas/callbacks';

import { apiClient } from './api';

// Re-export shared types under admin-friendly aliases (no T suffix) —
// preserva contratos com Callbacks.tsx + CallbackDetail.tsx + tests.
export type { CallbackExecutionStatus };
export type CallbackListItem = CallbackListItemT;
export type CallbackListResponse = CallbackListResponseT;
export type CallbackDetail = CallbackDetailT;
export type CallbackMessagePreview = CallbackMessagePreviewT;

// ─── Admin-only types (não vêm do backend; concept exclusivo da UI) ──────

export interface CallbackListFilters {
  statuses: CallbackExecutionStatus[];
  campaignId: string;
  scheduledFrom: string;
  scheduledTo: string;
  customerPhone: string;
}

export const EMPTY_CALLBACK_FILTERS: CallbackListFilters = {
  statuses: [],
  campaignId: '',
  scheduledFrom: '',
  scheduledTo: '',
  customerPhone: '',
};

export const ALL_CALLBACK_STATUSES: CallbackExecutionStatus[] = [
  'pending',
  'executing',
  'executed',
  'failed',
  'cancelled',
  'expired',
];

/**
 * Serialize filters + pagination into the querystring expected by
 * `GET /admin/callbacks`. `scheduled_from`/`scheduled_to` from a
 * `datetime-local` input are normalised to ISO 8601 UTC.
 */
export function toCallbackQueryString(
  f: CallbackListFilters,
  page: number,
  pageSize: number,
): string {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));
  if (f.statuses.length > 0) params.set('status', f.statuses.join(','));
  if (f.campaignId) params.set('campaign_id', f.campaignId);
  if (f.scheduledFrom) params.set('scheduled_from', new Date(f.scheduledFrom).toISOString());
  if (f.scheduledTo) params.set('scheduled_to', new Date(f.scheduledTo).toISOString());
  if (f.customerPhone) params.set('customer_phone', f.customerPhone);
  return params.toString();
}

export const callbacksApi = {
  list: (qs: string) => apiClient.get<CallbackListResponse>(`/admin/callbacks?${qs}`),
  get: (id: string) => apiClient.get<CallbackDetail>(`/admin/callbacks/${id}`),
  cancel: (id: string, reason?: string) =>
    apiClient.post<CallbackDetail>(`/admin/callbacks/${id}/cancel`, {
      reason: reason || undefined,
    }),
  retry: (id: string) => apiClient.post<CallbackDetail>(`/admin/callbacks/${id}/retry`),
};
